import React, { useEffect, useState, useCallback } from 'react';
import { X, PlayCircle, Headphones, BookOpen, ChefHat, Heart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRmContent } from '../../hooks';
import { useUserFavoritesContext } from '../../contexts/UserFavoritesContext';
import { detectKindFromUrl, wrapVimeoUrlForNative } from '../../utils/urlUtils';
import RmVideoViewer from './RmVideoViewer';
import RmAudioViewer from './RmAudioViewer';
import RmArticleViewer from './RmArticleViewer';
import RmRecipeViewer from './RmRecipeViewer';
import type { RmContentDocument } from '../../types/rmContent';

const typeConfig: Record<RmContentDocument['type'], { label: string; icon: React.ElementType; className: string }> = {
  videos:   { label: 'Video',   icon: PlayCircle,  className: 'bg-blue-50 text-blue-700 border-blue-200' },
  audios:   { label: 'Audio',   icon: Headphones,   className: 'bg-purple-50 text-purple-700 border-purple-200' },
  articles: { label: 'Article', icon: BookOpen,      className: 'bg-rm-primary-700/10 text-rm-primary-700 border-rm-primary-700/20' },
  recipes:  { label: 'Recipe',  icon: ChefHat,       className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

interface ContentModalProps {
  url: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  allowCredentials?: boolean;
  trustDomain?: boolean;
}

const ContentModal: React.FC<ContentModalProps> = ({
  url,
  title,
  isOpen,
  onClose,
  allowCredentials = true,
  trustDomain = false,
}) => {
  const { user } = useAuth();
  const language = user?.language || 'en';
  const { content, isLoading, isRmContent } = useRmContent(url, isOpen, language);
  const { getResourceByUrl, toggleFavoriteFlag, createFavorite } = useUserFavoritesContext();
  const [togglingHeart, setTogglingHeart] = useState(false);

  // Look up the favorite record for the current URL
  const favoriteRecord = url ? getResourceByUrl(url) : undefined;

  const isFavorited = favoriteRecord?.payload?.metadata?.isFavorited === true;

  const displayTitle = (isRmContent && content?.title) || title || 'External Content';

  const handleToggleHeart = useCallback(async () => {
    if (togglingHeart) return;
    setTogglingHeart(true);
    try {
      if (favoriteRecord) {
        await toggleFavoriteFlag(favoriteRecord.id);
      } else {
        const kind = detectKindFromUrl(url);
        const thumbnail = (content && 'image' in content && content.image?.src) ? content.image.src : undefined;
        await createFavorite({
          kind,
          payload: {
            itemId: url,
            title: displayTitle,
            url,
            ...(thumbnail ? { thumbnail } : {}),
            metadata: {
              link: url,
              isFavorited: true,
              source: 'modal',
            },
          },
        });
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    } finally {
      setTogglingHeart(false);
    }
  }, [togglingHeart, favoriteRecord, toggleFavoriteFlag, url, createFavorite, displayTitle, content]);

  // Escape key & body scroll lock
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Sandbox permissions for iframe fallback (same as LinkModal)
  const getSandboxPermissions = () => {
    const perms = ['allow-scripts', 'allow-forms', 'allow-popups', 'allow-popups-to-escape-sandbox'];
    if (allowCredentials) {
      perms.push('allow-same-origin', 'allow-storage-access-by-user-activation');
    }
    if (trustDomain) {
      perms.push('allow-top-navigation', 'allow-top-navigation-by-user-activation', 'allow-modals', 'allow-downloads');
    }
    return perms.join(' ');
  };

  const getReferrerPolicy = (): React.HTMLAttributeReferrerPolicy =>
    allowCredentials ? 'same-origin' : 'strict-origin-when-cross-origin';

  if (!isOpen) return null;

  const renderBody = () => {
    // Loading state
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-rm-primary-700" />
            <span className="text-sm text-gray-500">Loading content…</span>
          </div>
        </div>
      );
    }

    // RM content found — render native viewer (no padding — viewers own their layout for full-bleed heroes)
    if (isRmContent && content) {
      return (
        <div className="overflow-y-auto h-full">
          {content.type === 'videos' && <RmVideoViewer content={content} />}
          {content.type === 'audios' && <RmAudioViewer content={content} />}
          {content.type === 'articles' && <RmArticleViewer content={content} />}
          {content.type === 'recipes' && <RmRecipeViewer content={content} />}
        </div>
      );
    }

    // Fallback — sandboxed iframe (identical to LinkModal). On native, Vimeo links are
    // served through an allowlisted HTTPS wrapper page.
    return (
      <iframe
        src={wrapVimeoUrlForNative(url)}
        className="w-full h-full border-0"
        title={title || 'External Content'}
        sandbox={getSandboxPermissions()}
        loading="lazy"
        referrerPolicy={getReferrerPolicy()}
        allow={allowCredentials ? 'storage-access; camera; microphone; geolocation; fullscreen; picture-in-picture' : 'fullscreen; picture-in-picture'}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_200ms_ease-out]" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full h-full sm:w-[90vw] sm:h-[90vh] sm:max-w-6xl bg-white sm:rounded-xl shadow-2xl overflow-hidden flex flex-col animate-[modalIn_250ms_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {isRmContent && content && (() => {
              const cfg = typeConfig[content.type];
              const Icon = cfg.icon;
              return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border flex-shrink-0 ${cfg.className}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {cfg.label}
                </span>
              );
            })()}
            <h2 className="text-base font-semibold text-rm-ink-900 truncate">{displayTitle}</h2>
          </div>
          <div className="flex items-center space-x-2 ml-4 group">
            {/* Heart toggle */}
            <button
              onClick={handleToggleHeart}
              disabled={togglingHeart}
              className={`flex-shrink-0 p-2 rounded-full transition-colors ${
                isFavorited
                  ? 'text-red-500 hover:text-red-400'
                  : 'text-gray-400 hover:text-red-500 hover:bg-gray-200'
              } ${togglingHeart ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              {togglingHeart ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
              )}
            </button>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {renderBody()}
        </div>
      </div>
    </div>
  );
};

export default ContentModal;
